import {
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  AnimatePresence,
  type Variants,
} from 'framer-motion';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Landing-page animation primitives. Adapts peddlum's scroll-reveal architecture
 * to the Knowlez design system, with peddlum's consumer "personality" stripped
 * for JP B2B gravitas: small translations, one shared calm easing, no springs /
 * bounce / rotation-on-entry / infinite content loops, and `once: true` always.
 * Every primitive honours `prefers-reduced-motion` by rendering the final,
 * fully-visible state with no transition.
 */
export { AnimatePresence };

export const EASE = [0.16, 0.84, 0.44, 1] as const; // calm, fast-out, no overshoot
export const DUR = { subtle: 0.3, standard: 0.4, reveal: 0.6 } as const;
export const VIEWPORT = { once: true, margin: '-60px' } as const;

export const fadeInUp: Variants = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } };
export const fadeIn: Variants = { hidden: { opacity: 0 }, visible: { opacity: 1 } };
export const fadeInLeft: Variants = { hidden: { opacity: 0, x: -24 }, visible: { opacity: 1, x: 0 } };
export const fadeInRight: Variants = { hidden: { opacity: 0, x: 24 }, visible: { opacity: 1, x: 0 } };
export const scaleIn: Variants = { hidden: { opacity: 0, scale: 0.97 }, visible: { opacity: 1, scale: 1 } };
export const blurIn: Variants = {
  hidden: { opacity: 0, filter: 'blur(6px)' },
  visible: { opacity: 1, filter: 'blur(0px)' },
};

export const staggerContainer = (stagger = 0.07, delayChildren = 0): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: stagger, delayChildren } },
});

/** The workhorse — reveal-on-scroll, reduced-motion-safe. */
export function Reveal({
  children,
  variant = fadeInUp,
  delay = 0,
  duration = DUR.reveal,
  className,
}: {
  children: ReactNode;
  variant?: Variants;
  delay?: number;
  duration?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT}
      variants={variant}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/** Stagger a grid/list; pair with <StaggerItem>. */
export function Stagger({
  children,
  stagger = 0.07,
  delayChildren = 0,
  className,
}: {
  children: ReactNode;
  stagger?: number;
  delayChildren?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT}
      variants={staggerContainer(stagger, delayChildren)}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  variant = fadeInUp,
  className,
}: {
  children: ReactNode;
  variant?: Variants;
  className?: string;
}) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div className={className} variants={variant} transition={{ duration: DUR.standard, ease: EASE }}>
      {children}
    </motion.div>
  );
}

/** Number that counts up once on viewport entry (JP "metrics counting up"). */
export function CountUp({
  to,
  durationMs = 1600,
  prefix = '',
  suffix = '',
  decimals = 0,
  className,
}: {
  to: number;
  durationMs?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const [val, setVal] = useState(reduce ? to : 0);
  const [started, setStarted] = useState(reduce);

  useEffect(() => {
    if (started || !ref.current) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setStarted(true);
      },
      { threshold: 0.4 },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started || reduce) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - t0) / durationMs, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(to * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, to, durationMs, reduce]);

  return (
    <span ref={ref} className={`num tabular-nums ${className ?? ''}`}>
      {prefix}
      {val.toLocaleString(undefined, { maximumFractionDigits: decimals, minimumFractionDigits: decimals })}
      {suffix}
    </span>
  );
}

/** Thin top scroll-progress bar — engineered, not playful. */
export function ScrollProgressBar() {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4 });
  if (reduce) return null;
  return (
    <motion.div
      aria-hidden
      style={{ scaleX, transformOrigin: '0%' }}
      className="fixed top-0 inset-x-0 h-[2px] bg-accent-600 z-50"
    />
  );
}

/** Hanko seal that "presses" in — the brand stamp, used as section accents. */
export function HankoStamp({
  size = 96,
  rotate = -6,
  delay = 0,
  className,
  kanji = '知',
}: {
  size?: number;
  rotate?: number;
  delay?: number;
  className?: string;
  kanji?: string;
}) {
  const reduce = useReducedMotion();
  const style = { width: size, height: size, fontSize: size * 0.42 };
  if (reduce) {
    return (
      <span className={`hanko ${className ?? ''}`} style={{ ...style, transform: `rotate(${rotate}deg)` }} aria-hidden>
        {kanji}
      </span>
    );
  }
  return (
    <motion.span
      aria-hidden
      className={`hanko ${className ?? ''}`}
      style={style}
      initial={{ opacity: 0, scale: 1.6, rotate: rotate - 10 }}
      whileInView={{ opacity: 1, scale: 1, rotate }}
      viewport={VIEWPORT}
      transition={{
        opacity: { duration: 0.2, delay },
        scale: { duration: 0.45, delay, ease: [0.34, 1.2, 0.64, 1] }, // one tiny press
        rotate: { duration: 0.45, delay, ease: EASE },
      }}
    >
      {kanji}
    </motion.span>
  );
}
